import type { ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Briefcase, FileText, ShieldCheck } from "lucide-react";
// ADV-1171: relocated from /warranties/:id to /warranty-claims/:id (ADR-0047).
// This page describes the REMEDIATION WORK (production dates, crew, urgency,
// reported issues) — never coverage. Coverage lives on the parent obligation.
import { useWarrantyClaim } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { EntityActivityFeed } from "@/components/v2/entity-activity";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function LinkedChip({
  icon,
  label,
  sublabel,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left hover:bg-[color:var(--color-elevated)] w-full sm:w-1/2"
    >
      <span className="shrink-0 text-[color:var(--color-text-md)]">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
          {label}
        </span>
        <span className="block typo-label text-[color:var(--color-text-md)]">
          {sublabel}
        </span>
      </span>
    </button>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

// Render the warranty_issues jsonb blob as a flat key/value list. The shape is
// not strongly typed (free-form import payload), so we coerce defensively.
function issueEntries(value: unknown): { label: string; detail: string }[] {
  if (!value || typeof value !== "object") return [];
  const obj = value as Record<string, unknown>;
  return Object.entries(obj)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => ({
      label: k.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase()),
      detail: typeof v === "string" ? v : JSON.stringify(v),
    }));
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function V2WarrantyClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useWarrantyClaim(id ?? "");
  const claim = (data as { warranty_claim?: Row } | undefined)?.warranty_claim;

  const displayId = str(claim?.display_id);
  const customerName = str(claim?.customer_name);
  const status = str(claim?.warranty_status);
  const projectType = str(claim?.project_type);
  const urgency = str(claim?.urgency);
  const initialReport = str(claim?.initial_report);
  const productionStart = str(claim?.production_start);
  const productionEnd = str(claim?.production_end);
  const warrantyComplete = str(claim?.warranty_complete);
  const rescheduleDate = str(claim?.reschedule_date);
  const customerFee = num(claim?.customer_fee_amount);
  const teamPay = num(claim?.team_pay_amount);
  const jobId = str(claim?.job_id);
  const proposalId = str(claim?.proposal_id);
  const warrantyId = str(claim?.warranty_id);
  // Customer detail route resolves on contact_id (not contact_role_id), so the
  // chip links via the resolved customer_contact_id surfaced by the read API.
  const customerContactId = str(claim?.customer_contact_id);

  const title = displayId ? `Warranty Claim ${displayId}` : "Warranty Claim";

  // Production window — these are CREW dates (when remediation work happened),
  // never coverage. ADR-0047 §150-156: production dates stop being labeled
  // "Coverage" anywhere in the product.
  const productionWindow =
    productionStart || productionEnd
      ? `${productionStart ? formatDate(productionStart) : "—"} – ${
          productionEnd ? formatDate(productionEnd) : "—"
        }`
      : "—";

  const issues = issueEntries(claim?.warranty_issues);

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !claim}
      title={title}
      subtitle={customerName ?? undefined}
      breadcrumbs={[
        { label: "Warranties", href: "/warranties" },
        { label: title },
      ]}
      notFoundMessage={{
        title: "Warranty claim not found",
        description: "This claim may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Warranty Claim" />}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* Inline properties strip */}
          <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
            {status && <StatusBadge status={status} />}
            {projectType && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>{projectType}</span>
              </>
            )}
            {claim?.created_at != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>Created {formatDate(claim.created_at as string)}</span>
              </>
            )}
          </div>

          {/* KPI row */}
          <KPIRow
            cards={[
              {
                label: "Status",
                value: status ? titleCase(status) : "—",
              },
              {
                label: "Project Type",
                value: projectType ?? "—",
              },
              {
                label: "Production Window",
                value: productionWindow,
              },
              {
                label: "Customer Fee",
                value: customerFee != null ? formatCurrency(customerFee) : "—",
              },
            ]}
          />

          {/* Overview */}
          {(initialReport || urgency) && (
            <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3 space-y-2">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Overview
              </span>
              {urgency && (
                <div className="typo-body text-[color:var(--color-text-md)]">
                  Urgency:{" "}
                  <span className="text-[color:var(--color-text-hi)]">
                    {titleCase(urgency)}
                  </span>
                </div>
              )}
              {initialReport && (
                <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                  {initialReport}
                </p>
              )}
            </div>
          )}

          {/* Production — these are crew dates, not coverage. */}
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
            <div className="px-4 pt-3">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Production
              </span>
            </div>
            <div className="px-4 py-2">
              <InfoRow
                label="Production Start"
                value={productionStart ? formatDate(productionStart) : null}
              />
              <InfoRow
                label="Production End"
                value={productionEnd ? formatDate(productionEnd) : null}
              />
              <InfoRow
                label="Warranty Complete"
                value={warrantyComplete ? formatDate(warrantyComplete) : null}
              />
              <InfoRow
                label="Reschedule Date"
                value={rescheduleDate ? formatDate(rescheduleDate) : null}
              />
              {!productionStart &&
                !productionEnd &&
                !warrantyComplete &&
                !rescheduleDate && (
                  <p className="py-1 typo-body text-[color:var(--color-text-lo)]">
                    No production dates recorded.
                  </p>
                )}
            </div>
          </div>

          {/* Linked records — parent obligation gets first priority */}
          {(warrantyId ||
            jobId ||
            proposalId ||
            (customerName && customerContactId)) && (
            <div className="flex flex-wrap gap-2">
              {warrantyId && (
                <LinkedChip
                  icon={<ShieldCheck className="h-4 w-4" />}
                  label="View warranty"
                  sublabel="Parent Obligation"
                  onClick={() => navigate(`/warranties/${warrantyId}`)}
                />
              )}
              {jobId && (
                <LinkedChip
                  icon={<Briefcase className="h-4 w-4" />}
                  label="View job"
                  sublabel="Linked Job"
                  onClick={() => navigate(`/jobs/${jobId}`)}
                />
              )}
              {proposalId && (
                <LinkedChip
                  icon={<FileText className="h-4 w-4" />}
                  label="View proposal"
                  sublabel="Linked Proposal"
                  onClick={() => navigate(`/proposals/${proposalId}`)}
                />
              )}
              {customerName && customerContactId && (
                <LinkedChip
                  icon={<User className="h-4 w-4" />}
                  label={customerName}
                  sublabel="Customer"
                  onClick={() => navigate(`/customers/${customerContactId}`)}
                />
              )}
            </div>
          )}

          {/* Reported issues (free-form warranty_issues jsonb) */}
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
            <div className="px-4 pt-3">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Issues
              </span>
            </div>
            <div className="mt-2">
              {issues.length === 0 ? (
                <p className="px-4 py-3 typo-body text-[color:var(--color-text-lo)]">
                  No issues reported on this claim.
                </p>
              ) : (
                issues.map((issue, i) => (
                  <div
                    key={i}
                    className="px-4 py-3 border-t border-[color:var(--color-border)]"
                  >
                    <div className="typo-body font-medium text-[color:var(--color-text-hi)]">
                      {issue.label}
                    </div>
                    <div className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                      {issue.detail}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Activity timeline — full feed at bottom of main column.
              ADV-1180 added warranty_claim to communicationEntityTypes, so the
              feed mounts cleanly here (previously suppressed in ADV-1144). */}
          {id && (
            <RailSection title="Activity">
              <EntityActivityFeed
                entityType="warranty_claim"
                entityId={id}
                emptyDescription="Messages, calls, and notes on this warranty claim appear here once the first contact is made."
              />
            </RailSection>
          )}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Properties">
            <InfoRow label="Status" value={status ? titleCase(status) : null} />
            <InfoRow label="Customer" value={customerName} />
            <InfoRow label="Claim" value={displayId} />
            <InfoRow label="Project Type" value={projectType} />
            <InfoRow
              label="Urgency"
              value={urgency ? titleCase(urgency) : null}
            />
            <InfoRow
              label="Production Start"
              value={productionStart ? formatDate(productionStart) : null}
            />
            <InfoRow
              label="Production End"
              value={productionEnd ? formatDate(productionEnd) : null}
            />
            <InfoRow
              label="Warranty Complete"
              value={warrantyComplete ? formatDate(warrantyComplete) : null}
            />
            <InfoRow
              label="Customer Fee"
              value={customerFee != null ? formatCurrency(customerFee) : null}
            />
            <InfoRow
              label="Team Pay"
              value={teamPay != null ? formatCurrency(teamPay) : null}
            />
            <InfoRow
              label="Created"
              value={
                claim?.created_at
                  ? formatDate(claim.created_at as string)
                  : null
              }
            />
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
